import { useEditor } from '@craftjs/core';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface PageSettingsData {
  /** Page title (for custom pages or template slide title override) */
  title: string;
  /** Education category (only applicable for custom pages) */
  category?: string;
}

/**
 * Hook to track whether the editor has unsaved changes.
 * Compares current serialized state against initial state,
 * and optionally tracks page settings (title, category) changes.
 *
 * @param slideKey - A key that identifies the current slide (e.g., slide index).
 *                   When this changes, the baseline is reset to the new slide's state.
 * @param pageSettings - Optional current page settings to track
 * @param initialPageSettings - Optional initial page settings to compare against
 */
export function useHasUnsavedChanges(
  slideKey: string | number,
  pageSettings?: PageSettingsData,
  initialPageSettings?: PageSettingsData
) {
  const { query } = useEditor();
  const initialStateRef = useRef<string | null>(null);
  const initialPageSettingsRef = useRef<PageSettingsData | undefined>(
    initialPageSettings
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Store query in a ref to avoid re-running the baseline capture effect
  // when the query reference changes during editing
  const queryRef = useRef(query);
  queryRef.current = query;

  // Capture/reset baseline state when slideKey changes
  // This ensures we're comparing against the current slide's initial state,
  // not a stale state from a previous slide
  useEffect(() => {
    // Small delay to ensure Craft.js has initialized with new data
    const timeoutId = setTimeout(() => {
      initialStateRef.current = queryRef.current.serialize();
      setHasChanges(false);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [slideKey]);

  // Update initial page settings ref when it changes (e.g., on slide change)
  useEffect(() => {
    initialPageSettingsRef.current = initialPageSettings;
  }, [initialPageSettings]);

  // Check if page settings have changed from their initial values
  const checkPageSettingsChanges = useCallback(() => {
    if (!pageSettings || !initialPageSettingsRef.current) return false;

    const titleChanged =
      pageSettings.title !== initialPageSettingsRef.current.title;
    const categoryChanged =
      pageSettings.category !== initialPageSettingsRef.current.category;

    return titleChanged || categoryChanged;
  }, [pageSettings]);

  // Check for changes (editor state + page settings)
  const checkForChanges = useCallback(() => {
    // Check editor state changes
    const editorHasChanges =
      initialStateRef.current !== null &&
      query.serialize() !== initialStateRef.current;

    // Check page settings changes
    const settingsHaveChanges = checkPageSettingsChanges();

    return editorHasChanges || settingsHaveChanges;
  }, [query, checkPageSettingsChanges]);

  // Mark as saved (reset the baseline for both editor and page settings)
  const markAsSaved = useCallback(() => {
    initialStateRef.current = query.serialize();
    if (pageSettings) {
      initialPageSettingsRef.current = { ...pageSettings };
    }
    setHasChanges(false);
  }, [query, pageSettings]);

  // Update hasChanges when checking
  const updateHasChanges = useCallback(() => {
    setHasChanges(checkForChanges());
  }, [checkForChanges]);

  return { hasChanges, checkForChanges, markAsSaved, updateHasChanges };
}
